/**
 * Shared compact card grid + "View details" modal for Football 3D week results.
 * Used by: week-finished view (Football3dWeekResultsBoard) and Schedule Results tab (mobile).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getFootball3dTeamLogoUrl } from 'shared/utils/cdn';
import { Button } from 'shared/components/ui/button';
import { FootballEventResultsModal } from 'features/markets/components/bets-area/FootballEventResultsModal';
import { FOOTBALL3D_YELLOW } from 'features/markets/components/bets-area/Football3dArea';

function formatTime(ts) {
  if (ts == null || ts === '') return '';
  const d = new Date(Number(ts) * 1000);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * @param {Array<{ eventId: string, result: object | null, homeCode: string, awayCode: string, homeLogoUrl: string, awayLogoUrl: string, scoreLabel: string, featured: boolean, eventTimestamp?: number | string }} rows
 * @param {boolean} [showWeekHeader=false]
 * @param {number|string} [weekNumber]
 */
export default function FootballWeekResultsCardGrid({ rows = [], showWeekHeader = false, weekNumber }) {
  const { t } = useTranslation();
  const [modalRow, setModalRow] = React.useState(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = React.useState(false);

  const list = Array.isArray(rows) ? rows : [];

  return (
    <div className="w-full">
      {showWeekHeader && weekNumber != null && (
        <div className="p-3 border-b border-border c7-sport-gradient">
          <div className="text-[var(--sport-on-color)] font-semibold">
            {(t('WEEK_LABEL') || 'Week')} {weekNumber}
          </div>
          <div className="text-[var(--sport-on-color)]/80 text-xs">
            {t('RESULTS') || 'Results'}
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground">
          {t('NO_RESULTS_YET') || 'No results yet.'}
        </div>
      ) : (
        <div className="grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((row) => (
            <div
              key={row.eventId}
              className="rounded-xl border border-border bg-card text-card-foreground p-3 shadow-sm transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    {row.featured && (
                      <i className="bi bi-camera-video-fill text-sm shrink-0" style={{ color: FOOTBALL3D_YELLOW }} aria-hidden title="Live Stream" />
                    )}
                    <span>
                      {(t('EVENT_NUMBER') || 'Event')} #{row.eventId}
                      {row.eventTimestamp != null && row.eventTimestamp !== '' ? ` • ${formatTime(row.eventTimestamp)}` : ''}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 min-w-0">
                    <img src={row.homeLogoUrl} alt="" className="w-8 h-8 object-contain shrink-0 rounded" />
                    <span className="font-semibold text-sm truncate">{row.homeCode}</span>
                    <span className="text-muted-foreground text-xs">vs</span>
                    <span className="font-semibold text-sm truncate">{row.awayCode}</span>
                    <img src={row.awayLogoUrl} alt="" className="w-8 h-8 object-contain shrink-0 rounded" />
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-xs text-muted-foreground">{t('EXACT_SCORE') || 'Exact score'}</div>
                  <div className="text-lg font-extrabold text-foreground tabular-nums">{row.scoreLabel}</div>
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setModalRow(row.result);
                    setIsResultsModalOpen(true);
                  }}
                  disabled={!row.result}
                >
                  {(t('VIEW_DETAILS') || 'View details')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <FootballEventResultsModal
        open={isResultsModalOpen}
        onClose={() => setIsResultsModalOpen(false)}
        onCloseComplete={() => setModalRow(null)}
        row={modalRow}
        weekNumber={weekNumber}
      />
    </div>
  );
}
